import React, { useId } from 'react';
import './DWLFLoader.css';

const CHART_POINTS = [
  { x: 0, y: 70 },
  { x: 50, y: 55 },
  { x: 100, y: 62 },
  { x: 160, y: 30 },
  { x: 220, y: 45 },
  { x: 280, y: 20 },
  { x: 340, y: 35 },
  { x: 380, y: 15 },
];

// Dots at key data points (indices into CHART_POINTS)
const DOT_INDICES = [1, 2, 3, 5, 7];
const DOT_COLORS = ['', '--green', '', '--red', '--green'];

function buildPath(points) {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx1 = prev.x + (curr.x - prev.x) * 0.4;
    const cpx2 = curr.x - (curr.x - prev.x) * 0.4;
    d += ` C ${cpx1} ${prev.y}, ${cpx2} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  return d;
}

function buildAreaPath(points, baseY) {
  const linePath = buildPath(points);
  const last = points[points.length - 1];
  const first = points[0];
  return `${linePath} L ${last.x} ${baseY} L ${first.x} ${baseY} Z`;
}

export default function DWLFLoader({ size = 'medium', showText = true, overlay = false }) {
  const uid = useId();
  const glowId = `dwlf-glow-${uid}`;
  const gradId = `dwlf-area-grad-${uid}`;

  // Tiny size: minimal branded dot animation for tight spaces
  if (size === 'tiny') {
    const tinyLoader = (
      <div className="dwlf-loader dwlf-loader--tiny">
        <svg className="dwlf-loader__svg" viewBox="0 0 20 16" preserveAspectRatio="xMidYMid meet">
          <circle className="dwlf-loader__tiny-dot" cx="4" cy="8" r="1.5" />
          <circle className="dwlf-loader__tiny-dot dwlf-loader__tiny-dot--2" cx="10" cy="8" r="1.5" />
          <circle className="dwlf-loader__tiny-dot dwlf-loader__tiny-dot--3" cx="16" cy="8" r="1.5" />
        </svg>
      </div>
    );
    if (overlay) {
      return (
        <div className="dwlf-loader-overlay">
          {tinyLoader}
        </div>
      );
    }
    return tinyLoader;
  }

  const viewBox = '0 0 400 100';
  const pathD = buildPath(CHART_POINTS);
  const areaD = buildAreaPath(CHART_POINTS, 100);

  const loader = (
    <div className={`dwlf-loader dwlf-loader--${size}`}>
      <svg className="dwlf-loader__svg" viewBox={viewBox} preserveAspectRatio="xMidYMid meet">
        <defs>
          {/* Glow filter */}
          <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feColorMatrix in="blur" type="matrix"
              values="0 0 0 0 0  0 0.7 0.85 0 0  0 0 0 0 0  0 0 0 1 0" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Area gradient */}
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00b4d8" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#00b4d8" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Subtle grid */}
        {[20, 40, 60, 80].map(y => (
          <line key={`h${y}`} className="dwlf-loader__grid-line" x1="0" y1={y} x2="400" y2={y} />
        ))}
        {[80, 160, 240, 320].map(x => (
          <line key={`v${x}`} className="dwlf-loader__grid-line" x1={x} y1="0" x2={x} y2="100" />
        ))}

        {/* Area fill */}
        <path className="dwlf-loader__area" d={areaD} fill={`url(#${gradId})`} />

        {/* Main chart line */}
        <path className="dwlf-loader__line" d={pathD} style={{ filter: `url(#${glowId})` }} />

        {/* Pulse rings */}
        <g>
          {DOT_INDICES.map((pi, i) => {
            const p = CHART_POINTS[pi];
            return <circle key={`ring${i}`} className="dwlf-loader__dot-ring" cx={p.x} cy={p.y} r="2.5" />;
          })}
        </g>

        {/* Data point dots */}
        <g>
          {DOT_INDICES.map((pi, i) => {
            const p = CHART_POINTS[pi];
            return (
              <circle
                key={`dot${i}`}
                className={`dwlf-loader__dot dwlf-loader__dot${DOT_COLORS[i]}`}
                cx={p.x} cy={p.y} r="2.5"
                style={{ filter: `url(#${glowId})` }}
              />
            );
          })}
        </g>

        {/* DWLF text */}
        {showText && size !== 'small' && (
          <text className="dwlf-loader__text" x="200" y="92">DWLF</text>
        )}
      </svg>
    </div>
  );

  if (overlay) {
    return (
      <div className="dwlf-loader-overlay">
        {loader}
      </div>
    );
  }

  return loader;
}
